/**
 * Tech Management Entity - RTK Query API
 * API endpoints for IT Asset & Change Management domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { archiveArgs } from '../../../shared/api/archiveArgs';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

const extractResults = (response) => response?.results || response || [];

// TEC-14 (D7): contract documents are streamed by the tenant-scoped download
// action -- never a storage URL. Fetched as a blob through the authenticated
// base query, then saved via a temporary object URL.
const filenameFromDisposition = (header) => {
  if (!header) return null;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1] || null;
};

const saveBlob = (blob, filename) => {
  if (typeof window === 'undefined' || !window.URL?.createObjectURL) return;
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
};

// TEC-14 (D7): vendor contracts go up as multipart so a document can ride
// along. Empty values are skipped (contract_value / contract_owner reject '');
// contract_document is only appended when it is a newly picked File, never the
// {name, size} object the API returns. Content-Type is left to the browser.
const buildVendorContractFormData = (data) => {
  const form = new FormData();
  Object.entries(data || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'contract_document') {
      if (typeof File !== 'undefined' && value instanceof File) form.append(key, value);
      return;
    }
    if (typeof value === 'object') return;
    form.append(key, typeof value === 'boolean' ? String(value) : value);
  });
  return form;
};

// TEC-16: remediate takes an optional change request; never send an empty id.
const buildRemediateDriftBody = ({ notes, change_request_id }) =>
  change_request_id ? { notes: notes || '', change_request_id } : { notes: notes || '' };

export const techManagementApiTestUtils = {
  normalizeListResponse,
  extractResults,
  filenameFromDisposition,
  buildVendorContractFormData,
  buildRemediateDriftBody,
};

export const techManagementApi = createApi({
  reducerPath: 'techManagementApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'ITAsset',
    'ITAssetCategory',
    'SoftwareLicense',
    'ChangeRequest',
    'ChangeApproval',
    'ChangeTask',
    'Incident',
    'Problem',
    'ServiceRequest',
    'ConfigurationItem',
    'TechStats',
    'Release',
    'DeploymentLog',
    'Vendor',
    'VendorContract',
    'VendorRiskAssessment',
    'ConfigBaseline',
    'ConfigDrift',
    'AccessReview',
    'WorkflowTransition',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // IT ASSETS
    // =========================================================================

    /**
     * Get paginated list of IT assets
     */
    getAssets: builder.query({
      query: ({ page = 1, limit = 20, category = '', status = '', search = '' } = {}) => ({
        url: '/api/v1/tech/assets/',
        params: {
          page,
          page_size: limit,
          category: category || undefined,
          status: status || undefined,
          search: search || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ITAsset', id })),
              { type: 'ITAsset', id: 'LIST' },
            ]
          : [{ type: 'ITAsset', id: 'LIST' }],
    }),

    /**
     * Get single asset by ID
     */
    getAssetById: builder.query({
      query: (id) => `/api/v1/tech/assets/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ITAsset', id }],
    }),

    /**
     * Create new asset
     */
    createAsset: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/assets/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ITAsset', id: 'LIST' }, { type: 'TechStats' }],
    }),

    /**
     * Update asset
     */
    updateAsset: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/tech/assets/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ITAsset', id },
        { type: 'ITAsset', id: 'LIST' },
      ],
    }),

    /**
     * Delete asset
     */
    deleteAsset: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/tech/assets/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'ITAsset', id: 'LIST' }, { type: 'TechStats' }],
    }),

    /**
     * Get asset categories
     */
    getAssetCategories: builder.query({
      query: () => '/api/v1/tech/asset-categories/',
      transformResponse: extractResults,
      providesTags: [{ type: 'ITAssetCategory', id: 'LIST' }],
    }),

    // =========================================================================
    // CHANGE REQUESTS
    // =========================================================================

    /**
     * Get change requests
     */
    getChangeRequests: builder.query({
      query: ({ page = 1, limit = 20, status = '', priority = '', type = '' } = {}) => ({
        url: '/api/v1/tech/change-requests/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
          priority: priority || undefined,
          change_type: type || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ChangeRequest', id })),
              { type: 'ChangeRequest', id: 'LIST' },
            ]
          : [{ type: 'ChangeRequest', id: 'LIST' }],
    }),

    /**
     * Get change request by ID
     */
    getChangeRequestById: builder.query({
      query: (id) => `/api/v1/tech/change-requests/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'ChangeRequest', id }],
    }),

    /**
     * Create change request
     */
    createChangeRequest: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/change-requests/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ChangeRequest', id: 'LIST' }, { type: 'TechStats' }],
    }),

    /**
     * Update change request
     */
    updateChangeRequest: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/tech/change-requests/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
      ],
    }),

    /**
     * Submit change for approval
     */
    // Approval routing C2 (Task 9): `id` or `{ id, approvers }` (picks keyed by flow step
    // order). Opens the approval from draft, or a new round from returned.
    submitChangeForApproval: builder.mutation({
      query: (arg) => {
        const { id, approvers } = typeof arg === 'object' ? arg : { id: arg };
        return {
          url: `/api/v1/tech/change-requests/${encodeURIComponent(id)}/submit/`,
          method: 'POST',
          ...(approvers ? { body: { approvers } } : {}),
        };
      },
      invalidatesTags: (result, error, arg) => {
        const id = typeof arg === 'object' ? arg.id : arg;
        return [
          { type: 'ChangeRequest', id },
          { type: 'ChangeRequest', id: 'LIST' },
        ];
      },
    }),

    /** C2: a returned change is withdrawn by its requester or an admin. */
    withdrawChangeRequest: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/change-requests/${encodeURIComponent(id)}/withdraw/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
      ],
    }),

    /**
     * Approve change request (CAB member).
     *
     * TEC-11: this used to take an `action` param and always POST to
     * /approve/ regardless of its value -- so a caller passing
     * action: 'reject' would silently *approve* the change instead
     * (the backend approve endpoint has no `action` field at all; it's a
     * distinct endpoint from reject). Never had a caller, so never fired
     * in production, but the hook itself was wrong. Split into this
     * approve-only mutation and a separate rejectChangeRequest below,
     * matching the two distinct backend endpoints
     * (apps/tech_management/views.py ChangeRequestViewSet.approve/.reject).
     */
    approveChangeRequest: builder.mutation({
      query: ({ id, comments }) => ({
        url: `/api/v1/tech/change-requests/${id}/approve/`,
        method: 'POST',
        body: { comments },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
        { type: 'ChangeApproval', id: 'LIST' },
      ],
    }),

    /**
     * Reject change request (CAB member). TEC-11: previously unreachable --
     * no FE caller at all.
     */
    rejectChangeRequest: builder.mutation({
      query: ({ id, comments }) => ({
        url: `/api/v1/tech/change-requests/${id}/reject/`,
        method: 'POST',
        body: { comments },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
        { type: 'ChangeApproval', id: 'LIST' },
      ],
    }),

    /**
     * Start implementing an approved change. TEC-11: previously unreachable.
     */
    startChangeImplementation: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/change-requests/${id}/start_implementation/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
      ],
    }),

    /**
     * Mark an implementing/testing change as completed. TEC-11: previously
     * unreachable.
     */
    completeChangeRequest: builder.mutation({
      query: ({ id, notes }) => ({
        url: `/api/v1/tech/change-requests/${id}/complete/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
      ],
    }),

    /**
     * Roll back a change. TEC-11: previously unreachable.
     */
    rollbackChangeRequest: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/v1/tech/change-requests/${id}/rollback/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ChangeRequest', id },
        { type: 'ChangeRequest', id: 'LIST' },
      ],
    }),

    /**
     * Changes awaiting this tenant's CAB approval. TEC-11: previously
     * unreachable -- no FE caller of change-requests/pending_approval/.
     */
    getPendingApprovalChanges: builder.query({
      query: () => '/api/v1/tech/change-requests/pending_approval/',
      transformResponse: extractResults,
      providesTags: [{ type: 'ChangeRequest', id: 'PENDING_APPROVAL' }],
    }),

    /**
     * Changes requested by the current user. TEC-11: previously
     * unreachable -- no FE caller of change-requests/my_changes/.
     */
    getMyChanges: builder.query({
      query: () => '/api/v1/tech/change-requests/my_changes/',
      transformResponse: extractResults,
      providesTags: [{ type: 'ChangeRequest', id: 'MY_CHANGES' }],
    }),

    /**
     * Start a change implementation task. TEC-11: change-tasks endpoints
     * previously had no FE caller at all. Tasks are rendered nested inside
     * the parent change's detail response (ChangeRequestDetailSerializer),
     * not via a standalone list query, so `changeRequestId` is required
     * purely to invalidate that parent's cache entry and pull the updated
     * task status back in.
     */
    startChangeTask: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/tech/change-tasks/${id}/start/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { id, changeRequestId }) => [
        { type: 'ChangeTask', id },
        { type: 'ChangeRequest', id: changeRequestId },
      ],
    }),

    /**
     * Complete a change implementation task. TEC-11: change-tasks endpoints
     * previously had no FE caller at all. See startChangeTask for why
     * `changeRequestId` is required.
     */
    completeChangeTask: builder.mutation({
      query: ({ id, notes }) => ({
        url: `/api/v1/tech/change-tasks/${id}/complete/`,
        method: 'POST',
        body: { notes },
      }),
      invalidatesTags: (result, error, { id, changeRequestId }) => [
        { type: 'ChangeTask', id },
        { type: 'ChangeRequest', id: changeRequestId },
      ],
    }),
    // =========================================================================
    // CONFIGURATION BASELINES / ITEMS / DRIFT (TEC-16)
    // =========================================================================

    /**
     * Configuration items. TEC-16: a ConfigItem is a setting inside a baseline,
     * not a CMDB record; the endpoint filters on baseline / category /
     * is_mandatory. This used to send ci_type / status, which it ignores.
     */
    getConfigItems: builder.query({
      query: ({ page = 1, limit = 20, baseline, category, isMandatory } = {}) => ({
        url: '/api/v1/tech/config-items/',
        params: {
          page,
          page_size: limit,
          baseline: baseline || undefined,
          category: category || undefined,
          is_mandatory: isMandatory === undefined ? undefined : String(isMandatory),
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'ConfigurationItem', id })),
              { type: 'ConfigurationItem', id: 'LIST' },
            ]
          : [{ type: 'ConfigurationItem', id: 'LIST' }],
    }),

    getConfigItemById: builder.query({
      query: (id) => `/api/v1/tech/config-items/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ConfigurationItem', id }],
    }),

    // TEC-16: getConfigItemRelationships was removed -- it passed a ConfigItem id
    // as `source_asset` to asset-relationships/, which relates Assets.

    createConfigItem: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/config-items/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { baseline }) => [
        { type: 'ConfigurationItem', id: 'LIST' },
        { type: 'ConfigBaseline', id: baseline },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    deleteConfigItem: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/tech/config-items/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { baseline }) => [
        { type: 'ConfigurationItem', id: 'LIST' },
        { type: 'ConfigBaseline', id: baseline },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    getConfigBaselines: builder.query({
      query: ({ page = 1, limit = 20, status, asset, search } = {}) => ({
        url: '/api/v1/tech/config-baselines/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
          asset: asset || undefined,
          search: search || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'ConfigBaseline', id: 'LIST' }],
    }),

    getConfigBaselineById: builder.query({
      query: (id) => `/api/v1/tech/config-baselines/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ConfigBaseline', id }],
    }),

    createConfigBaseline: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/config-baselines/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'ConfigBaseline', id: 'LIST' }],
    }),

    activateConfigBaseline: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/config-baselines/${id}/activate/`,
        method: 'POST',
      }),
      // Activation supersedes the asset's previous active baseline, so the
      // whole list changes, not just this row.
      invalidatesTags: (result, error, id) => [
        { type: 'ConfigBaseline', id },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    captureConfigBaselineSnapshot: builder.mutation({
      query: ({ id, snapshot }) => ({
        url: `/api/v1/tech/config-baselines/${id}/capture_snapshot/`,
        method: 'POST',
        body: { snapshot },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ConfigBaseline', id },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    getConfigDrifts: builder.query({
      query: ({ page = 1, limit = 20, status, severity, baseline } = {}) => ({
        url: '/api/v1/tech/config-drifts/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
          severity: severity || undefined,
          baseline: baseline || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'ConfigDrift', id: 'LIST' }],
    }),

    getOpenConfigDrifts: builder.query({
      query: () => '/api/v1/tech/config-drifts/open_drifts/',
      transformResponse: extractResults,
      providesTags: [{ type: 'ConfigDrift', id: 'LIST' }],
    }),

    /** Record a drift observed against a baseline item (no automatic scanner yet). */
    createConfigDrift: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/config-drifts/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ConfigDrift', id: 'LIST' },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    acknowledgeConfigDrift: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/config-drifts/${id}/acknowledge/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'ConfigDrift', id: 'LIST' }],
    }),

    remediateConfigDrift: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/tech/config-drifts/${id}/remediate/`,
        method: 'POST',
        body: buildRemediateDriftBody(data),
      }),
      invalidatesTags: [
        { type: 'ConfigDrift', id: 'LIST' },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    acceptConfigDriftRisk: builder.mutation({
      query: ({ id, justification }) => ({
        url: `/api/v1/tech/config-drifts/${id}/accept_risk/`,
        method: 'POST',
        body: { justification },
      }),
      invalidatesTags: [
        { type: 'ConfigDrift', id: 'LIST' },
        { type: 'ConfigBaseline', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // STATISTICS
    // =========================================================================

    /**
     * Get tech management dashboard statistics
     */
    getTechStats: builder.query({
      query: () => '/api/v1/tech/change-requests/pending_approval/',
      providesTags: [{ type: 'TechStats' }],
    }),

    /**
     * Get asset summary by category
     */
    getAssetSummary: builder.query({
      query: () => '/api/v1/tech/assets/by_criticality/',
      providesTags: [{ type: 'ITAsset', id: 'SUMMARY' }],
    }),

    /**
     * Active vendor contracts ending within the next 90 days. TEC-14: was
     * getExpiringLicenses, which sent a `days` param the backend ignores (the
     * window is fixed at 90 days) and had no caller.
     */
    getExpiringContracts: builder.query({
      query: () => '/api/v1/tech/vendor-contracts/expiring_soon/',
      transformResponse: extractResults,
      providesTags: [{ type: 'VendorContract', id: 'EXPIRING' }],
    }),

    // =========================================================================
    // RELEASES
    // =========================================================================
    getReleases: builder.query({
      query: ({ page = 1, limit = 20, status = '' } = {}) => ({
        url: '/api/v1/tech/releases/',
        params: { page, page_size: limit, status: status || undefined },
      }),
      transformResponse: extractResults,
      providesTags: [{ type: 'Release', id: 'LIST' }],
    }),

    getReleaseById: builder.query({
      query: (id) => `/api/v1/tech/releases/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Release', id }],
    }),

    createRelease: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/releases/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Release', id: 'LIST' }],
    }),

    deployRelease: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/tech/releases/${id}/deploy/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'Release', id: 'LIST' },
        { type: 'DeploymentLog', id: 'LIST' },
      ],
    }),

    getDeploymentLogs: builder.query({
      query: ({ page = 1, limit = 20, release = '' } = {}) => ({
        url: '/api/v1/tech/deployment-logs/',
        params: { page, page_size: limit, release: release || undefined },
      }),
      transformResponse: extractResults,
      providesTags: [{ type: 'DeploymentLog', id: 'LIST' }],
    }),

    // =========================================================================
    // VENDORS
    // =========================================================================
    getVendors: builder.query({
      // TEC-13: previously ignored a `risk_tier` arg VendorsPage passed in --
      // VendorViewSet's filterset_fields is `criticality`, not `risk_tier`,
      // so the Risk Tier filter buttons silently did nothing server-side.
      query: ({ page = 1, limit = 20, search = '', criticality } = {}) => ({
        url: '/api/v1/tech/vendors/',
        params: { page, page_size: limit, search: search || undefined, criticality },
      }),
      transformResponse: extractResults,
      providesTags: [{ type: 'Vendor', id: 'LIST' }],
    }),

    createVendor: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/vendors/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Vendor', id: 'LIST' }],
    }),

    // TEC-14: vendor detail, edit, terminate and the risk/review queues.
    getVendorById: builder.query({
      query: (id) => `/api/v1/tech/vendors/${encodeURIComponent(id)}/`,
      providesTags: (result, error, id) => [{ type: 'Vendor', id }],
    }),

    updateVendor: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/tech/vendors/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Vendor', id },
        { type: 'Vendor', id: 'LIST' },
      ],
    }),

    /** D17: also terminates the vendor's active contracts. */
    terminateVendor: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/vendors/${id}/terminate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Vendor', id },
        { type: 'Vendor', id: 'LIST' },
        { type: 'VendorContract', id: 'LIST' },
        { type: 'VendorContract', id: 'EXPIRING' },
      ],
    }),

    getHighRiskVendors: builder.query({
      query: () => '/api/v1/tech/vendors/high_risk/',
      transformResponse: extractResults,
      providesTags: [{ type: 'Vendor', id: 'LIST' }],
    }),

    getVendorsRequiringReview: builder.query({
      query: () => '/api/v1/tech/vendors/requiring_review/',
      transformResponse: extractResults,
      providesTags: [{ type: 'Vendor', id: 'LIST' }],
    }),

    getVendorContracts: builder.query({
      query: ({ vendor, status, page = 1, limit = 20 } = {}) => ({
        url: '/api/v1/tech/vendor-contracts/',
        params: {
          page,
          page_size: limit,
          vendor: vendor || undefined,
          status: status || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'VendorContract', id: 'LIST' }],
    }),

    createVendorContract: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/vendor-contracts/',
        method: 'POST',
        body: buildVendorContractFormData(data),
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'Vendor', id: 'LIST' },
        { type: 'VendorContract', id: 'LIST' },
        { type: 'VendorContract', id: 'EXPIRING' },
      ],
    }),

    updateVendorContract: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/tech/vendor-contracts/${id}/`,
        method: 'PATCH',
        body: buildVendorContractFormData(data),
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'Vendor', id: 'LIST' },
        { type: 'VendorContract', id: 'LIST' },
        { type: 'VendorContract', id: 'EXPIRING' },
      ],
    }),

    deleteVendorContract: builder.mutation({
      // D24: archives; an optional reason is sent in the request body.
      query: ({ id, reason }) => ({
        url: `/api/v1/tech/vendor-contracts/${id}/`,
        method: 'DELETE',
        body: reason ? { reason } : undefined,
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'Vendor', id: 'LIST' },
        { type: 'VendorContract', id: 'LIST' },
        { type: 'VendorContract', id: 'EXPIRING' },
      ],
    }),

    downloadVendorContractDocument: builder.mutation({
      async queryFn({ id, filename }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: `/api/v1/tech/vendor-contracts/${id}/download/`,
          responseHandler: (response) => (response.ok ? response.blob() : response.json()),
        });
        if (result.error) return { error: result.error };
        const name =
          filenameFromDisposition(result.meta?.response?.headers?.get('content-disposition')) ||
          filename ||
          'contract-document';
        saveBlob(result.data, name);
        return { data: { filename: name } };
      },
    }),

    createVendorRiskAssessment: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/vendor-risk-assessments/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'VendorRiskAssessment', id: 'LIST' },
      ],
    }),

    // An editable (draft, in-progress or returned) assessment; send only changed fields.
    updateVendorRiskAssessment: builder.mutation({
      query: ({ id, vendor, ...data }) => ({
        url: `/api/v1/tech/vendor-risk-assessments/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'VendorRiskAssessment', id: 'LIST' },
      ],
    }),

    // C2 (Task 10): draft/in_progress, or returned (resubmit); `approvers` when a step
    // needs a pick (400 needs_choice otherwise).
    submitVendorRiskAssessment: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/tech/vendor-risk-assessments/${encodeURIComponent(id)}/submit/`,
        method: 'POST',
        ...(approvers ? { body: { approvers } } : {}),
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'VendorRiskAssessment', id: 'LIST' },
      ],
    }),

    /** D9: refused (403) for the assessor/creator; only a submitted assessment. */
    approveVendorRiskAssessment: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/tech/vendor-risk-assessments/${id}/approve/`,
        method: 'POST',
      }),
      // Approval rewrites the vendor's risk rating and next review date.
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'Vendor', id: 'LIST' },
        { type: 'VendorRiskAssessment', id: 'LIST' },
      ],
    }),

    /** C2: a returned assessment is withdrawn by its assessor/creator or an admin. */
    withdrawVendorRiskAssessment: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/tech/vendor-risk-assessments/${encodeURIComponent(id)}/withdraw/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'VendorRiskAssessment', id: 'LIST' },
      ],
    }),

    /** D21: withdraw a draft/in-progress/submitted assessment (not approved). */
    cancelVendorRiskAssessment: builder.mutation({
      query: ({ id, reason }) => ({
        url: `/api/v1/tech/vendor-risk-assessments/${id}/cancel/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { vendor }) => [
        { type: 'Vendor', id: vendor },
        { type: 'VendorRiskAssessment', id: 'LIST' },
      ],
    }),

    // =========================================================================
    // ACCESS REVIEWS
    // =========================================================================
    getAccessReviews: builder.query({
      query: ({ page = 1, limit = 20, status = '' } = {}) => ({
        url: '/api/v1/tech/access-reviews/',
        params: { page, page_size: limit, status: status || undefined },
      }),
      transformResponse: extractResults,
      providesTags: [{ type: 'AccessReview', id: 'LIST' }],
    }),

    createAccessReview: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/access-reviews/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'AccessReview', id: 'LIST' }],
    }),

    // TEC-16: access-review campaign lifecycle and item decisions.
    getAccessReviewById: builder.query({
      query: (id) => `/api/v1/tech/access-reviews/${id}/`,
      providesTags: (result, error, id) => [{ type: 'AccessReview', id }],
    }),

    getActiveAccessReviews: builder.query({
      query: () => '/api/v1/tech/access-reviews/active/',
      transformResponse: extractResults,
      providesTags: [{ type: 'AccessReview', id: 'LIST' }],
    }),

    getOverdueAccessReviews: builder.query({
      query: () => '/api/v1/tech/access-reviews/overdue/',
      transformResponse: extractResults,
      providesTags: [{ type: 'AccessReview', id: 'LIST' }],
    }),

    startAccessReview: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/access-reviews/${id}/start/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'AccessReview', id },
        { type: 'AccessReview', id: 'LIST' },
      ],
    }),

    /** 400 with the undecided-item count while any item is still pending. */
    completeAccessReview: builder.mutation({
      query: (id) => ({
        url: `/api/v1/tech/access-reviews/${id}/complete/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'AccessReview', id },
        { type: 'AccessReview', id: 'LIST' },
      ],
    }),

    createAccessReviewItem: builder.mutation({
      query: (data) => ({
        url: '/api/v1/tech/access-review-items/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { review }) => [
        { type: 'AccessReview', id: review },
        { type: 'AccessReview', id: 'LIST' },
      ],
    }),

    /** D9: refused for the user under review; a decided item can't be re-decided. */
    decideAccessReviewItem: builder.mutation({
      query: ({ id, decision, justification }) => ({
        url: `/api/v1/tech/access-review-items/${id}/decide/`,
        method: 'POST',
        body: { decision, justification: justification || '' },
      }),
      invalidatesTags: (result, error, { reviewId }) => [
        { type: 'AccessReview', id: reviewId },
        { type: 'AccessReview', id: 'LIST' },
      ],
    }),

    /**
     * Records that a revoke/modify was carried out in the source system. The
     * backend only sets a flag; it does not change anyone's access itself.
     */
    markAccessReviewItemExecuted: builder.mutation({
      query: ({ id }) => ({
        url: `/api/v1/tech/access-review-items/${id}/execute/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { reviewId }) => [{ type: 'AccessReview', id: reviewId }],
    }),
  }),
});

// Export hooks for components
export const {
  // Assets
  useGetAssetsQuery,
  useGetAssetByIdQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useGetAssetCategoriesQuery,
  // Change Requests
  useGetChangeRequestsQuery,
  useGetChangeRequestByIdQuery,
  useCreateChangeRequestMutation,
  useUpdateChangeRequestMutation,
  useSubmitChangeForApprovalMutation,
  useWithdrawChangeRequestMutation,
  useApproveChangeRequestMutation,
  useRejectChangeRequestMutation,
  useStartChangeImplementationMutation,
  useCompleteChangeRequestMutation,
  useRollbackChangeRequestMutation,
  useGetPendingApprovalChangesQuery,
  useGetMyChangesQuery,
  useStartChangeTaskMutation,
  useCompleteChangeTaskMutation,
  // Configuration baselines / items / drift (TEC-16)
  useGetConfigItemsQuery,
  useGetConfigItemByIdQuery,
  useCreateConfigItemMutation,
  useDeleteConfigItemMutation,
  useGetConfigBaselinesQuery,
  useGetConfigBaselineByIdQuery,
  useCreateConfigBaselineMutation,
  useActivateConfigBaselineMutation,
  useCaptureConfigBaselineSnapshotMutation,
  useGetConfigDriftsQuery,
  useGetOpenConfigDriftsQuery,
  useCreateConfigDriftMutation,
  useAcknowledgeConfigDriftMutation,
  useRemediateConfigDriftMutation,
  useAcceptConfigDriftRiskMutation,
  // Stats
  useGetTechStatsQuery,
  useGetAssetSummaryQuery,
  useGetExpiringContractsQuery,
  // Releases
  useGetReleasesQuery,
  useGetReleaseByIdQuery,
  useCreateReleaseMutation,
  useDeployReleaseMutation,
  useGetDeploymentLogsQuery,
  // Vendors
  useGetVendorsQuery,
  useCreateVendorMutation,
  useGetVendorByIdQuery,
  useUpdateVendorMutation,
  useTerminateVendorMutation,
  useGetHighRiskVendorsQuery,
  useGetVendorsRequiringReviewQuery,
  useGetVendorContractsQuery,
  useCreateVendorContractMutation,
  useUpdateVendorContractMutation,
  useDeleteVendorContractMutation,
  useDownloadVendorContractDocumentMutation,
  useCreateVendorRiskAssessmentMutation,
  useUpdateVendorRiskAssessmentMutation,
  useSubmitVendorRiskAssessmentMutation,
  useApproveVendorRiskAssessmentMutation,
  useCancelVendorRiskAssessmentMutation,
  useWithdrawVendorRiskAssessmentMutation,
  // Access Reviews
  useGetAccessReviewsQuery,
  useCreateAccessReviewMutation,
  useGetAccessReviewByIdQuery,
  useGetActiveAccessReviewsQuery,
  useGetOverdueAccessReviewsQuery,
  useStartAccessReviewMutation,
  useCompleteAccessReviewMutation,
  useCreateAccessReviewItemMutation,
  useDecideAccessReviewItemMutation,
  useMarkAccessReviewItemExecutedMutation,
} = techManagementApi;
